import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  LinearProgress,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Divider,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  Stack,
  Avatar,
} from '@mui/material'
import {
  Add as AddIcon,
  Calculate as CalculateIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Payment as PayIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Lock as LockIcon,
  Close as CloseIcon,
  CalendarToday,
  AccountBalance,
} from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import PayrollCalculationDialog from '../../components/Payroll/PayrollCalculationDialog'
import apiClient, { Employee, payrollAPI, employeesAPI, manualPayrollAPI } from '../../services/api'
import { MobileDataCard } from '../../components/Common/MobileDataCard'
import type { MobileDataCardAction, MobileDataCardField } from '../../components/Common/MobileDataCard'

interface PayrollRecord {
  id: number
  employee_id: number
  employee_name: string
  employee_id_number: string
  pay_period_year: number
  pay_period_month: number
  gross_pay: number
  deductions: number
  net_pay: number
  status: 'draft' | 'calculated' | 'approved' | 'paid' | 'cancelled'
  is_draft?: boolean
  pay_date?: string
  notes?: string
  created_at: string
  updated_at: string
  payroll_items?: any[]
  employee?: any
}

interface PayrollSummary {
  total_employees: number
  total_gross_pay: number
  total_net_pay: number
  total_deductions: number
  average_gross_pay: number
  average_net_pay: number
  payroll_by_status?: Record<string, number>
  compensation_breakdown?: Record<string, any>
  bonus_summary?: Record<string, any>
  deduction_summary?: Record<string, any>
  department_summary?: Record<string, any>
  period?: string
}

const PayrollManagementPage: React.FC = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  
  const [payrollRecords, setPayrollRecords] = useState<PayrollRecord[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [payrollSummary, setPayrollSummary] = useState<PayrollSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [calculationDialogOpen, setCalculationDialogOpen] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  })
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRecord, setSelectedRecord] = useState<PayrollRecord | null>(null)
  const [speedDialOpen, setSpeedDialOpen] = useState(false)
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editNotes, setEditNotes] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false)
  const [selectedRecords, setSelectedRecords] = useState<number[]>([])

  useEffect(() => {
    loadPayrollData()
    loadEmployees()
  }, [selectedPeriod])

  const loadPayrollData = async () => {
    setLoading(true)
    try {
      const records = await payrollAPI.getPayrollRecords({
        year: selectedPeriod.year,
        month: selectedPeriod.month,
      })
      
      console.log('📥 RAW API Response - First Record:', records[0]);
      
      const summary = await payrollAPI.getSummary(selectedPeriod.year, selectedPeriod.month)
      
      const transformedRecords = records.map((record: any) => ({
        id: record.id,
        employee_id: record.employee_id,
        employee_name: record.employee?.full_name || `Employee ${record.employee_id}`,
        employee_id_number: record.employee?.employee_id || `EMP${record.employee_id.toString().padStart(3, '0')}`,
        pay_period_year: record.pay_period_year,
        pay_period_month: record.pay_period_month,
        gross_pay: record.gross_pay,
        deductions: record.deductions,
        net_pay: record.net_pay,
        status: record.status,
        is_draft: record.is_draft,
        pay_date: record.pay_date,
        notes: record.notes,
        created_at: record.created_at,
        updated_at: record.updated_at,
        payroll_items: record.payroll_items || [],
        employee: record.employee,
      }))
      
      setPayrollRecords(transformedRecords)
      setPayrollSummary({
        ...summary,
        payroll_by_status: summary.compensation_breakdown || {},
      })
    } catch (error) {
      console.error('Failed to load payroll data:', error)
      setPayrollRecords([])
      setPayrollSummary({
        total_employees: 0,
        total_gross_pay: 0,
        total_net_pay: 0,
        total_deductions: 0,
        average_gross_pay: 0,
        average_net_pay: 0,
        payroll_by_status: {},
      })
    } finally {
      setLoading(false)
    }
  }

  const loadEmployees = async () => {
    try {
      const { employees: employeesData } = await employeesAPI.getEmployees({
        status: 'active',
      })
      setEmployees(employeesData)
    } catch (error) {
      console.error('Failed to load employees:', error)
      setEmployees([])
    }
  }

  const handleCalculatePayroll = async (calculationData: any) => {
    try {
      const apiResult = await payrollAPI.calculatePayroll(calculationData)
      
      const transformedResult = {
        employee_id: apiResult.employee_id,
        pay_period_year: apiResult.pay_period_year,
        pay_period_month: apiResult.pay_period_month,
        calculation_summary: {
          base_pay: apiResult.calculation_summary?.base_pay || 0,
          overtime_pay: apiResult.calculation_summary?.overtime_pay || 0,
          bonus_amount: apiResult.calculation_summary?.bonus_amount || 0,
          total_deductions: apiResult.calculation_summary?.total_deductions || apiResult.total_deductions,
          gross_pay: apiResult.calculation_summary?.gross_pay || apiResult.gross_pay,
          net_pay: apiResult.calculation_summary?.net_pay || apiResult.net_pay,
          compensation_structure_type: apiResult.calculation_summary?.compensation_structure_type || 'unknown',
          base_rate: apiResult.calculation_summary?.base_rate || 0,
          hours_worked: apiResult.calculation_summary?.hours_worked || 0,
          days_worked: apiResult.calculation_summary?.days_worked || 0,
          overtime_hours: apiResult.calculation_summary?.overtime_hours || 0,
        },
        payroll_items: (apiResult.payroll_items || []).map((item: any) => ({
          payroll_type_id: item.payroll_type_id,
          amount: item.amount,
          hours: item.hours,
          rate: item.rate,
          description: item.description || '',
        })),
        warnings: apiResult.warnings || [],
      }
      
      return transformedResult
    } catch (error) {
      console.error('Payroll calculation failed:', error)
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('compensation structure')) {
        throw new Error('No active compensation structure found for this employee. Please set up compensation details first.')
      }
      throw error
    }
  }

  const handleCreatePayrollRecord = async (calculationResult: any) => {
    try {
      const response = await apiClient.post('/payroll/manual/calculate', {
        employee_ids: [calculationResult.employee_id],
        pay_period_year: calculationResult.pay_period_year,
        pay_period_month: calculationResult.pay_period_month,
      })
      
      console.log('Payroll record created:', response.data)
      await loadPayrollData()
    } catch (error) {
      console.error('Failed to create payroll record:', error)
      throw error
    }
  }

  const handleViewDetails = (record: PayrollRecord) => {
    setViewDetailsOpen(true)
    handleMenuClose()
  }

  const handleEditDraft = (record: PayrollRecord) => {
    setEditNotes(record.notes || '')
    setEditDialogOpen(true)
    handleMenuClose()
  }

  const handleSaveEdit = async () => {
    if (!selectedRecord) return
    
    try {
      await apiClient.put(`/payroll/manual/${selectedRecord.id}`, {
        notes: editNotes,
      })
      setEditDialogOpen(false)
      setSelectedRecord(null)
      await loadPayrollData()
    } catch (error) {
      console.error('Failed to update payroll record:', error)
      alert('Failed to update payroll record')
    }
  }

  const handleDeleteDraft = (record: PayrollRecord) => {
    setDeleteConfirmOpen(true)
    handleMenuClose()
  }

  const confirmDelete = async () => {
    if (!selectedRecord) return
    
    try {
      await apiClient.delete(`/payroll/manual/${selectedRecord.id}`)
      setDeleteConfirmOpen(false)
      setSelectedRecord(null)
      await loadPayrollData()
    } catch (error) {
      console.error('Failed to delete payroll record:', error)
      alert('Failed to delete payroll record')
    }
  }

  const handleFinalizeDraft = (record: PayrollRecord) => {
    setFinalizeConfirmOpen(true)
    handleMenuClose()
  }

  const confirmFinalize = async () => {
    if (!selectedRecord) return
    
    try {
      await apiClient.post('/payroll/manual/finalize', {
        payroll_record_ids: [selectedRecord.id],
      })
      setFinalizeConfirmOpen(false)
      setSelectedRecord(null)
      await loadPayrollData()
    } catch (error) {
      console.error('Failed to finalize payroll record:', error)
      alert('Failed to finalize payroll record')
    }
  }

  const handleDownloadPayslip = async (record: PayrollRecord) => {
    try {
      // For now, just show an alert. In production, this would generate a PDF
      alert(`Payslip download for ${selectedRecord?.employee_name} will be implemented soon.`)
      handleMenuClose()
      setSelectedRecord(null)
    } catch (error) {
      console.error('Failed to download payslip:', error)
      handleMenuClose()
      setSelectedRecord(null)
    }
  }

  const handleApprovePayroll = async (recordId: number) => {
    try {
      await payrollAPI.approvePayroll(recordId)
      loadPayrollData()
    } catch (error) {
      console.error('Failed to approve payroll:', error)
    }
  }

  const handleMarkAsPaid = async (recordId: number) => {
    try {
      await payrollAPI.markPayrollPaid(recordId)
      loadPayrollData()
    } catch (error) {
      console.error('Failed to mark as paid:', error)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
    }).format(amount)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default'
      case 'calculated': return 'info'
      case 'approved': return 'success'
      case 'paid': return 'primary'
      case 'cancelled': return 'error'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, record: PayrollRecord) => {
    console.log('🔍 Menu clicked - RAW record:', record);
    console.log('🔍 record.is_draft value:', record.is_draft);
    console.log('🔍 All record keys:', Object.keys(record));
    setAnchorEl(event.currentTarget)
    setSelectedRecord(record)
    console.log('🔍 selectedRecord after set:', record);
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    // Keep selectedRecord for dialogs that may need it
  }

  const SummaryCards = () => (
    <Grid container spacing={3} sx={{
      width: '100%',
      maxWidth: '100%',
      m: 0,
      mb: 4,
      '& .MuiGrid-item': {
        paddingLeft: '12px',
        paddingTop: '12px',
      }
    }}>
      <Grid item xs={12} sm={6}>
        <Card sx={{
          height: '100%',
          width: '100%',
          maxWidth: '100%',
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">{payrollRecords.length || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Employees
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <Card sx={{
          height: '100%',
          width: '100%',
          maxWidth: '100%',
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TrendingUpIcon color="info" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">
                  {formatCurrency(payrollRecords.reduce((sum, record) => sum + record.net_pay, 0))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Net Pay
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )

  // Render mobile card for a payroll record
  const renderMobilePayrollCard = (record: PayrollRecord) => {
    const fields: MobileDataCardField[] = [
      {
        label: 'Employee ID',
        value: record.employee_id_number,
        icon: <AccountBalance sx={{ fontSize: 16 }} />,
      },
      {
        label: 'Period',
        value: `${record.pay_period_month}/${record.pay_period_year}`,
        icon: <CalendarToday sx={{ fontSize: 16 }} />,
      },
      {
        label: 'Gross Pay',
        value: formatCurrency(record.gross_pay),
        variant: 'currency',
      },
      {
        label: 'Deductions',
        value: formatCurrency(record.deductions),
      },
      {
        label: 'Net Pay',
        value: formatCurrency(record.net_pay),
        variant: 'currency',
      },
      {
        label: 'Status',
        value: getStatusLabel(record.status),
        variant: 'chip',
        chipColor: getStatusColor(record.status) as any,
        chipVariant: 'filled',
      },
      {
        label: 'Pay Date',
        value: record.pay_date ? new Date(record.pay_date).toLocaleDateString() : 'Not set',
        variant: 'date',
      },
    ]

    const actions: MobileDataCardAction[] = [
      {
        label: 'View Details',
        icon: <ViewIcon />,
        onClick: () => {
          setSelectedRecord(record)
          handleViewDetails(record)
        },
      },
    ]

    if (record.is_draft) {
      actions.push(
        {
          label: 'Edit Draft',
          icon: <EditIcon />,
          onClick: () => {
            setSelectedRecord(record)
            handleEditDraft(record)
          },
        },
        {
          label: 'Finalize',
          icon: <LockIcon />,
          onClick: () => {
            setSelectedRecord(record)
            handleFinalizeDraft(record)
          },
        },
        {
          label: 'Delete Draft',
          icon: <DeleteIcon />,
          onClick: () => {
            setSelectedRecord(record)
            handleDeleteDraft(record)
          },
          color: 'error',
        }
      )
    }

    if (record.status === 'calculated') {
      actions.push({
        label: 'Approve',
        icon: <ApproveIcon />,
        onClick: () => handleApprovePayroll(record.id),
        color: 'success',
      })
    }

    if (record.status === 'approved') {
      actions.push({
        label: 'Mark as Paid',
        icon: <PayIcon />,
        onClick: () => handleMarkAsPaid(record.id),
        color: 'primary',
      })
    }

    actions.push({
      label: 'Download Payslip',
      icon: <DownloadIcon />,
      onClick: () => {
        setSelectedRecord(record)
        handleDownloadPayslip(record)
      },
    })

    return (
      <MobileDataCard
        key={record.id}
        title={record.employee_name}
        subtitle={`${record.pay_period_month}/${record.pay_period_year}`}
        avatar={
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            <MoneyIcon />
          </Avatar>
        }
        fields={fields}
        actions={actions}
        onClick={() => {
          setSelectedRecord(record)
          handleViewDetails(record)
        }}
      />
    )
  }

  const PayrollTable = () => (
    <Card sx={{
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">
            Payroll Records - {selectedPeriod.month}/{selectedPeriod.year}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedPeriod.year}
                label="Year"
                onChange={(e) => setSelectedPeriod(prev => ({ ...prev, year: Number(e.target.value) }))}
              >
                {[2023, 2024, 2025].map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedPeriod.month}
                label="Month"
                onChange={(e) => setSelectedPeriod(prev => ({ ...prev, month: Number(e.target.value) }))}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <MenuItem key={month} value={month}>
                    {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Mobile View - Card List */}
        {isMobile ? (
          <Stack spacing={2} sx={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}>
            {payrollRecords.length > 0 ? (
              payrollRecords.map((record) => renderMobilePayrollCard(record))
            ) : (
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No payroll records found</Typography>
              </Card>
            )}
          </Stack>
        ) : (
          /* Desktop/Tablet View - Table */
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              ...(isTablet && {
                '& table': {
                  minWidth: '900px',
                },
                overflowX: 'auto',
              }),
            }}
          >
            <Table sx={{
              width: '100%',
              maxWidth: '100%',
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Gross Pay</TableCell>
                  <TableCell align="right">Deductions</TableCell>
                  <TableCell align="right">Net Pay</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Pay Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payrollRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {record.employee_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {record.employee_id_number}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {record.pay_period_month}/{record.pay_period_year}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(record.gross_pay)}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(record.deductions)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(record.net_pay)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {record.is_draft ? (
                        <Chip
                          label="Draft"
                          color="warning"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label={getStatusLabel(record.status)}
                          color={getStatusColor(record.status) as any}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {record.pay_date ? new Date(record.pay_date).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, record)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )

  return (
    <Box sx={{
      p: { xs: 2, sm: 3 },
      pb: { xs: 10, sm: 3 },
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 3, sm: 4 },
        flexWrap: 'wrap',
        gap: 2,
      }}>
        <Typography
          variant="h4"
          component="h1"
          fontWeight="bold"
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' },
          }}
        >
          Payroll Management
        </Typography>
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<CalculateIcon />}
              onClick={() => setCalculationDialogOpen(true)}
              size="large"
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.5,
                fontWeight: 500,
                textTransform: 'none',
              }}
            >
              Calculate Payroll
            </Button>
          </Box>
        )}
      </Box>

      <SummaryCards />
      <PayrollTable />

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { handleViewDetails(selectedRecord!); }}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {selectedRecord?.is_draft && (
          <>
            <MenuItem onClick={() => { handleEditDraft(selectedRecord); }}>
              <EditIcon sx={{ mr: 1 }} />
              Edit Draft
            </MenuItem>
            <MenuItem onClick={() => { handleFinalizeDraft(selectedRecord); }}>
              <LockIcon sx={{ mr: 1 }} />
              Finalize
            </MenuItem>
            <MenuItem onClick={() => { handleDeleteDraft(selectedRecord); }}>
              <DeleteIcon sx={{ mr: 1 }} />
              Delete Draft
            </MenuItem>
          </>
        )}
        {selectedRecord?.status === 'calculated' && (
          <MenuItem onClick={() => { handleApprovePayroll(selectedRecord.id); handleMenuClose() }}>
            <ApproveIcon sx={{ mr: 1 }} />
            Approve
          </MenuItem>
        )}
        {selectedRecord?.status === 'approved' && (
          <MenuItem onClick={() => { handleMarkAsPaid(selectedRecord.id); handleMenuClose() }}>
            <PayIcon sx={{ mr: 1 }} />
            Mark as Paid
          </MenuItem>
        )}
        <MenuItem onClick={() => { handleDownloadPayslip(selectedRecord!); }}>
          <DownloadIcon sx={{ mr: 1 }} />
          Download Payslip
        </MenuItem>
      </Menu>

      {/* View Details Dialog */}
      <Dialog
        open={viewDetailsOpen}
        onClose={() => setViewDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Payroll Details</Typography>
            <IconButton onClick={() => { setViewDetailsOpen(false); setSelectedRecord(null); }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord && (
            <Box>
              <Grid container spacing={2} sx={{
                width: '100%',
                maxWidth: '100%',
                margin: 0,
                '& .MuiGrid-item': {
                  paddingLeft: '8px',
                  paddingTop: '8px',
                }
              }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Employee</Typography>
                  <Typography variant="body1">{selectedRecord.employee_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedRecord.employee_id_number}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Period</Typography>
                  <Typography variant="body1">
                    {selectedRecord.pay_period_month}/{selectedRecord.pay_period_year}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={getStatusLabel(selectedRecord.status)}
                      color={getStatusColor(selectedRecord.status) as any}
                      size="small"
                    />
                    {selectedRecord.is_draft && (
                      <Chip
                        label="Draft"
                        color="warning"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Pay Date</Typography>
                  <Typography variant="body1">
                    {selectedRecord.pay_date ? new Date(selectedRecord.pay_date).toLocaleDateString() : 'Not set'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Payment Summary</Typography>
              <Grid container spacing={2} sx={{
               width: '100%',
               maxWidth: '100%',
               margin: 0,
               '& .MuiGrid-item': {
                 paddingLeft: '8px',
                 paddingTop: '8px',
               }
             }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Gross Pay</Typography>
                  <Typography variant="h6" color="success.main">
                    {formatCurrency(selectedRecord.gross_pay)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Deductions</Typography>
                  <Typography variant="h6" color="error.main">
                    {formatCurrency(selectedRecord.deductions)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="subtitle2" color="text.secondary">Net Pay</Typography>
                  <Typography variant="h6" color="primary.main">
                    {formatCurrency(selectedRecord.net_pay)}
                  </Typography>
                </Grid>
              </Grid>

              {selectedRecord.payroll_items && selectedRecord.payroll_items.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Payroll Items</Typography>
                  <List dense>
                    {selectedRecord.payroll_items.map((item: any, index: number) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={item.description || item.payroll_type?.name || 'Item'}
                          secondary={`Amount: ${formatCurrency(item.amount)}${item.hours ? ` | Hours: ${item.hours}` : ''}${item.rate ? ` | Rate: ${formatCurrency(item.rate)}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {selectedRecord.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body2">{selectedRecord.notes}</Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setViewDetailsOpen(false); setSelectedRecord(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Draft Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false)
          setSelectedRecord(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Draft Payroll</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false)
            setSelectedRecord(null)
          }}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setSelectedRecord(null)
        }}
      >
        <DialogTitle>Delete Draft Payroll?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this draft payroll record for {selectedRecord?.employee_name}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteConfirmOpen(false)
            setSelectedRecord(null)
          }}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Finalize Confirmation Dialog */}
      <Dialog
        open={finalizeConfirmOpen}
        onClose={() => {
          setFinalizeConfirmOpen(false)
          setSelectedRecord(null)
        }}
      >
        <DialogTitle>Finalize Payroll?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Once finalized, this payroll record cannot be edited or deleted.
          </Alert>
          <Typography>
            Are you sure you want to finalize this payroll record for {selectedRecord?.employee_name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setFinalizeConfirmOpen(false)
            setSelectedRecord(null)
          }}>Cancel</Button>
          <Button onClick={confirmFinalize} color="primary" variant="contained">Finalize</Button>
        </DialogActions>
      </Dialog>

      {/* Payroll Calculation Dialog */}
      <PayrollCalculationDialog
        open={calculationDialogOpen}
        onClose={() => setCalculationDialogOpen(false)}
        onCalculate={handleCalculatePayroll}
        onCreateRecord={handleCreatePayrollRecord}
        employees={employees as any}
        loading={loading}
      />

      {/* Speed Dial for Actions - Mobile Only */}
      {isMobile && (
        <SpeedDial
          ariaLabel="Payroll Actions"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
          }}
          icon={<SpeedDialIcon />}
          onClose={() => setSpeedDialOpen(false)}
          onOpen={() => setSpeedDialOpen(true)}
          open={speedDialOpen}
        >
          <SpeedDialAction
            icon={<CalculateIcon />}
            tooltipTitle="Calculate Payroll"
            onClick={() => {
              setCalculationDialogOpen(true);
              setSpeedDialOpen(false);
            }}
          />
        </SpeedDial>
      )}
    </Box>
  )
}

export default PayrollManagementPage
